/**
 * Shared UI primitives for SetupGuard blocking screens.
 */

import React from 'react'

import { Link } from 'react-router-dom'

export function LoadingSpinner({ message }: { message: string }): React.ReactElement {
  return (
    <div className="h-screen overflow-y-auto flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-foreground-secondary">{message}</p>
      </div>
    </div>
  )
}

export function WarningIcon(): React.ReactElement {
  return (
    <svg className="w-12 h-12 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
      <path clipRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" fillRule="evenodd" />
    </svg>
  )
}

export function FiscalPeriodsInfo(): React.ReactElement {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
      <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">What are Fiscal Periods?</h3>
      <p className="text-sm text-blue-700 dark:text-blue-300">Fiscal periods are time-based &quot;buckets&quot; for organizing your transactions. They allow you to close books for completed periods, generate reports for specific timeframes, and maintain proper accounting controls.</p>
    </div>
  )
}

export function BlockingCard({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="h-screen overflow-y-auto flex items-center justify-center bg-background p-8">
      <div className="max-w-2xl w-full">
        <div className="bg-surface rounded-lg shadow-xl p-8 border-2 border-yellow-400">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
              <WarningIcon />
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

export function FiscalPeriodsRequired(): React.ReactElement {
  return (
    <BlockingCard>
      <h2 className="text-2xl font-bold text-foreground text-center mb-4">Fiscal Periods Required</h2>
      <p className="text-foreground-secondary text-center mb-6">Your organization doesn&apos;t have any fiscal periods configured yet. Fiscal periods are required to organize and track financial transactions properly.</p>
      <FiscalPeriodsInfo />
      <div className="flex justify-center">
        <Link className="inline-flex items-center gap-2 px-6 py-3 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium shadow-lg hover:shadow-xl" to="/periods">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 6v6m0 0v6m0-6h6m-6 0H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} /></svg>
          Create Fiscal Periods
        </Link>
      </div>
    </BlockingCard>
  )
}

export function isExemptPath(pathname: string): boolean {
  return (
    pathname === '/setup' ||
    pathname.startsWith('/setup/') ||
    pathname === '/organizations' ||
    pathname === '/periods' ||
    pathname === '/admin/periods' ||
    pathname.startsWith('/admin/periods/') ||
    pathname === '/admin/settings' ||
    pathname === '/admin/numbering'
  )
}
